/**
 * Offline save/sync engine for the MAIN app's Site Survey write path.
 *
 * Survey Reconciliation Phase 3 (M6/M7): every survey write + photo blob is
 * enqueued durably to IndexedDB (@officehub/offline-sdk MutationQueue +
 * PhotoStore) so a crash / tab-close / lost connection never loses on-site data.
 * A SyncEngine drains the queue; online screens drain immediately
 * (see surveyMutations.ts). Mirrors sales-client/src/lib/offlineEngine.ts.
 *
 * IndexedDB name prefix is `main-field` (sales-client uses `sales-field`). The
 * two apps are served from different origins so their IndexedDB is already
 * isolated; the distinct prefix removes any ambiguity if that ever changes.
 *
 * This outbox exists because an httpsCallable write that never resolved (crash /
 * tab close / lost signal) was simply lost. It is deliberately independent of
 * whatever local cache Firestore is configured with — that config has changed
 * three times (see client/src/config/firebase.ts) and this path was unaffected
 * every time, including the current `memoryLocalCache()` (#3775).
 */
import { MutationQueue, PhotoStore, SyncEngine } from '@officehub/offline-sdk';
import { getSurveySyncHandlers } from './surveySyncHandlers';

const DB_PREFIX = 'main-field';

/** Durable FIFO outbox of survey mutations (IndexedDB). */
export const surveyMutationQueue = new MutationQueue(`${DB_PREFIX}-mutations`);

/** Durable blob store for survey photos awaiting upload (IndexedDB, 50MB cap). */
export const surveyPhotoStore = new PhotoStore(`${DB_PREFIX}-photos`);

/** Drains the queue via the registered per-type survey sync handlers. */
export const surveySyncEngine = new SyncEngine({
  queue: surveyMutationQueue,
  photoStore: surveyPhotoStore,
  handlers: getSurveySyncHandlers({ photoStore: surveyPhotoStore }),
});

// Reap mutations stranded in 'syncing' by a tab that was killed mid-drain
// (sync() only reads 'pending', retryFailed() only 'failed' — a 'syncing' row is
// otherwise a silent write loss, Attack 7). Run once at module init, before
// SyncProvider's startup sync (~3s), so a reaped mutation is picked up on the
// first drain. The 90s age guard avoids clobbering another tab's genuinely
// in-flight mutation.
//
// Re-draining a reaped mutation is safe because EVERY currently-queued type is
// idempotent: create is a server-side no-op on clientMutationId; updateSection
// merges the same fields to the same values (issue #3230 — it used to replace
// the whole section map, which dropped untouched fields and photos);
// updateNoteCards / updateStatus / markSectionComplete / setLocation / saveMap
// are full field-replacements; removePhoto is an arrayRemove. addContact now
// carries a stable contactId that survey_addContact dedupes on (#4558), but it stays on
// the inline path until that server change is live everywhere; photos (server mints
// photoId) are deliberately NOT queued either.
void surveyMutationQueue.requeueStale(90_000).catch(() => {
  // Best-effort recovery; a failure here must not block engine construction.
});
